import { createContext, useState, useEffect } from 'react';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useGoogleLogin } from '@react-oauth/google';
import { config } from '@/config/env.config';

const GOOGLE_TOKEN_KEY = 'contract_management_google_token';
const GOOGLE_TOKEN_EXPIRY_KEY = 'contract_management_google_token_expiry';
const GOOGLE_SCOPE_VERSION_KEY = 'contract_management_google_scope_version';
const GOOGLE_SCOPE_VERSION = '2';

function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  const storedVersion = localStorage.getItem(GOOGLE_SCOPE_VERSION_KEY);
  if (storedVersion !== GOOGLE_SCOPE_VERSION) {
    localStorage.removeItem(GOOGLE_TOKEN_KEY);
    localStorage.removeItem(GOOGLE_TOKEN_EXPIRY_KEY);
    localStorage.setItem(GOOGLE_SCOPE_VERSION_KEY, GOOGLE_SCOPE_VERSION);
    return null;
  }
  const expiry = localStorage.getItem(GOOGLE_TOKEN_EXPIRY_KEY);
  if (expiry && Date.now() >= Number(expiry)) {
    localStorage.removeItem(GOOGLE_TOKEN_KEY);
    localStorage.removeItem(GOOGLE_TOKEN_EXPIRY_KEY);
    return null;
  }
  return localStorage.getItem(GOOGLE_TOKEN_KEY);
}

export const Route = createFileRoute('/_authenticated/contract-management')({
  component: ContractManagementPage,
});

export type ContractManagementRouteContext = {
  googleAccessToken?: string | null;
  onGoogleLogin?: () => void;
};

// Outlet do TanStack Router ignora a prop `context`; o filho lê via React context.
export const ContractManagementContext = createContext<ContractManagementRouteContext>({});

function ContractManagementPage() {
  if (config.google.clientId) {
    return <ContractManagementWithGoogle />;
  }
  return <Outlet />;
}

function ContractManagementWithGoogle() {
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const stored = getStoredToken();
    if (stored) setGoogleAccessToken(stored);
  }, []);

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      const token = tokenResponse.access_token;
      setGoogleAccessToken(token);
      localStorage.setItem(GOOGLE_TOKEN_KEY, token);
      localStorage.setItem(GOOGLE_SCOPE_VERSION_KEY, GOOGLE_SCOPE_VERSION);
      const expiresIn = tokenResponse.expires_in ?? 3600;
      localStorage.setItem(
        GOOGLE_TOKEN_EXPIRY_KEY,
        String(Date.now() + expiresIn * 1000),
      );
    },
    onError: () => {},
    scope: 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/documents',
  });

  return (
    <ContractManagementContext.Provider
      value={{ googleAccessToken, onGoogleLogin: googleLogin }}
    >
      <Outlet />
    </ContractManagementContext.Provider>
  );
}
